import { useEffect } from "react";

// Lets users click-and-drag a PrimeReact DataTable/TreeTable horizontally
// instead of only using the scrollbar - useful for grids with enough
// columns to overflow on most screens. Pair with the "drag-scroll-table"
// class (see Overwrites.scss) on the container to hide the native
// scrollbar, since dragging replaces it.
//
// Usage:
//   const tableContainerRef = useRef(null);
//   useDragScroll(tableContainerRef, [rows]);
//   <div ref={tableContainerRef} className="drag-scroll-table">
//     <DataTable value={rows} ... />
//   </div>
const useDragScroll = (containerRef, deps = []) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getScrollEl = () =>
      container.querySelector(".p-datatable-wrapper, .p-treetable-wrapper") ||
      container;

    let isDown = false;
    let startClientX = 0;
    let scrollLeft = 0;

    const onMouseDown = (e) => {
      if (e.target.closest("button, input, select, a, .p-paginator")) return;
      isDown = true;
      const el = getScrollEl();
      startClientX = e.clientX;
      scrollLeft = el.scrollLeft;
      container.style.cursor = "grabbing";
      container.style.userSelect = "none";
    };

    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      container.style.cursor = "grab";
      container.style.userSelect = "";
    };

    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const el = getScrollEl();
      el.scrollLeft = scrollLeft - (e.clientX - startClientX);
    };

    container.style.cursor = "grab";
    container.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    container.addEventListener("mousemove", onMouseMove);

    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      container.removeEventListener("mousemove", onMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, ...deps]);
};

export default useDragScroll;
